import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { darkBg, gridColor, labelColor, makeGradient, tooltipStyle } from "../api/echartsTheme";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../Global/other/formatSAST";

const color = "#FDB940";

export default function GDGraph({ graph_data }) {
  const chartRef = useRef(null);

  if (!graph_data) {
    return (
      <Grid item sx={{ p: 2 }}>
        <CircularProgress size={20} sx={{ color }} />
      </Grid>
    );
  }

  const seriesData = (graph_data.timestamps || []).map((ts, i) => [
    new Date(ts).getTime(),
    graph_data.gd_values[i] ?? null,
  ]);

  const option = {
    backgroundColor: darkBg,
    animation: false,
    grid: { top: 36, right: 24, bottom: 76, left: 64 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: "rgba(253,185,64,0.2)",
        handleStyle: { color: "#FDB940", borderColor: "#FDB940" },
        moveHandleStyle: { color: "#FDB940" },
        emphasis: { handleStyle: { color: "#FDB940" } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    tooltip: {
      trigger: "axis",
      ...tooltipStyle,
      formatter: (params) =>
        params
          .filter((p) => p.value !== null)
          .map((p) => `${p.seriesName}: <b>${Number(p.value[1]).toFixed(1)}%</b>`)
          .join("<br/>"),
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { lineStyle: { color: gridColor } },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => {
          const d = new Date(v);
          return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "%",
      nameTextStyle: { color: labelColor, fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10, formatter: (v) => `${v}%` },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series: [
      {
        name: "Geyser Demand %",
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { color, width: 2 },
        areaStyle: { color: makeGradient(color), opacity: 0.4 },
        itemStyle: { color },
        data: seriesData.filter(([, y]) => y !== null),
      },
    ],
  };

  const handleDownloadCsv = () => {
    if (!graph_data?.timestamps) return;
    let csv = "Timestamp (SAST),Geyser Demand (%)\r\n";
    graph_data.timestamps.forEach((ts, i) => {
      const val = graph_data.gd_values[i] != null ? Number(graph_data.gd_values[i]).toFixed(2) : "";
      csv += `${formatSAST(ts)},${val}\r\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Geyser_Demand_Pct_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: darkBg });
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `Geyser_Demand_Pct_${dayjs().format("YYYY-MM-DD")}.png`;
    a.click();
  };

  return (
    <Card sx={{ borderRadius: "12px", backgroundColor: darkBg }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 2, px: 2 }}>
        <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>
          Geyser Demand Percentage
        </Typography>
        <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
      </Box>
      <Grid item sx={{ width: "100%", p: 2 }}>
        <ReactECharts ref={chartRef} option={option} style={{ height: 400 }} notMerge />
      </Grid>
    </Card>
  );
}
